import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios, { AxiosError, AxiosResponse } from "axios";
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import ReactGA from "react-ga4";
import { Navigate, useNavigate } from "react-router-dom";

import SubscriptionModal, { SubscriptionModalType } from "../SubscriptionModal";

import UserContext from "context/UserContext";

import Button from "components/atoms/Button";
import Card from "components/atoms/Card";
import EstimatedMonthlyCost from "components/atoms/EstimatedMonthlyCost";
import Modal from "components/atoms/Modal";
import Spinner from "components/atoms/Spinner";
import SubscriptionCard from "components/molecules/SubscriptionCard";
import CheckoutForm from "components/organisms/CheckoutForm";

import { error, success } from "utils";
import {
  NUTRITION_PLATFORM_GROWTH_SLUG,
  NUTRITION_PLATFORM_PRO_SLUG,
  NUTRITION_PLATFORM_SLUG,
  NUTRITION_PLATFORM_STARTER_SLUG,
  uniqueEventId,
} from "utils/products";
import { CustomerStatusType } from "utils/types";

interface CouponInfo {
  valid: boolean;
  couponId?: string;
  promotionCodeId?: string;
  name?: string;
  percentOff?: number;
  amountOff?: number;
  currency?: string;
  duration?: string;
  durationInMonths?: number;
  message?: string;
}

interface PlatformTierConfig {
  tier: "starter" | "growth" | "pro";
  slug: string;
  name: string;
  price: number;
  priceDisplay: string;
  tokensMillions: number;
  refillPrice: string;
  refillPricePerM: number;
  description: string;
  features: PlatformTierFeature[];
}

interface PlatformTierFeature {
  label: string;
  tooltip?: string;
}

const PLATFORM_TIERS: PlatformTierConfig[] = [
  {
    tier: "starter",
    slug: NUTRITION_PLATFORM_STARTER_SLUG,
    name: "Starter",
    price: 99,
    priceDisplay: "$99",
    tokensMillions: 1,
    refillPrice: "$25 per 1M refill",
    refillPricePerM: 25,
    description: "Launch quickly with predictable pricing and core AI tools.",
    features: [
      {
        label: "Easy AI + API + SDK access",
        tooltip:
          "Use Passio REST APIs, SDKs, and build custom Easy AI functions using the same account and token budget.",
      },
      {
        label: "Localization support",
        tooltip:
          "Serve users in more markets with localized food responses and language-aware outputs.",
      },
      {
        label: "Email + Community Slack support",
        tooltip:
          "Standard support channel for implementation guidance and issue triage.",
      },
    ],
  },
  {
    tier: "growth",
    slug: NUTRITION_PLATFORM_GROWTH_SLUG,
    name: "Growth",
    price: 599,
    priceDisplay: "$599",
    tokensMillions: 5,
    refillPrice: "$10 per 1M refill",
    refillPricePerM: 10,
    description:
      "Scale confidently with better controls and lower refill cost.",
    features: [
      {
        label: "Everything in Starter",
        tooltip:
          "Includes all Starter capabilities, plus advanced optimization features for growing teams.",
      },
      {
        label: "Model switching",
        tooltip:
          "Choose default AI models by route to balance quality, speed, and cost.",
      },
      {
        label: "Insights & Settings dashboard",
        tooltip:
          "Track route-level usage, costs, and configure key account settings in one place.",
      },
      {
        label: "Dedicated Slack support",
        tooltip:
          "Direct support channel for your team with faster turnaround than community support.",
      },
    ],
  },
  {
    tier: "pro",
    slug: NUTRITION_PLATFORM_PRO_SLUG,
    name: "Pro",
    price: 2999,
    priceDisplay: "$2,999",
    tokensMillions: 100,
    refillPrice: "$2.50 per 1M refill",
    refillPricePerM: 2.5,
    description:
      "Maximize performance and efficiency for high-volume production.",
    features: [
      {
        label: "Everything in Growth",
        tooltip:
          "Keeps all Growth capabilities and adds Pro-only optimization and support benefits.",
      },
      {
        label: "AI Insights",
        tooltip:
          "Analyzes your route usage and model selections for each route, and provides suggestions on cost savings, unused routes you may want to try, and more.",
      },
      {
        label: "Enterprise Slack support",
        tooltip:
          "<8h response time on Slack for issues, usually ~15m during working hours.",
      },
      {
        label: "Lowest refill rate",
        tooltip:
          "Best token refill price per 1M to reduce blended cost at higher usage volumes.",
      },
    ],
  },
];

const FeatureTooltip = ({ text }: { text?: string }) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: -9999, left: -9999 });

  const tooltipCallbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node || !triggerRef.current) return;
      const r = triggerRef.current.getBoundingClientRect();
      const t = node.getBoundingClientRect();
      const gap = 8;
      const pad = 8;
      let top = r.bottom + gap;
      let left = r.left + r.width / 2 - t.width / 2;
      if (left < pad) left = pad;
      if (left + t.width > window.innerWidth - pad)
        left = window.innerWidth - pad - t.width;
      if (top + t.height > window.innerHeight - pad)
        top = r.top - t.height - gap;
      setCoords({ top, left });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visible]
  );

  if (!text) return null;
  return (
    <span
      ref={triggerRef}
      className="inline-flex items-center ml-1 cursor-help"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <QuestionMarkCircleIcon className="h-4 w-4 text-gray-600" />
      {visible &&
        createPortal(
          <div
            ref={tooltipCallbackRef}
            role="tooltip"
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              zIndex: 99999,
              pointerEvents: "none",
            }}
            className="w-72 max-w-[90vw] rounded-md bg-gray-900 text-white text-sm leading-relaxed px-3 py-2.5 shadow-lg"
          >
            {text}
          </div>,
          document.body
        )}
    </span>
  );
};

const TIER_CHECKOUT_INFO: Record<
  string,
  { name: string; price: string; tokens: string; refillPrice: string }
> = {
  [NUTRITION_PLATFORM_STARTER_SLUG]: {
    name: "Starter",
    price: "$99/mo",
    tokens: "1M tokens included",
    refillPrice: "$25 per 1M refill",
  },
  [NUTRITION_PLATFORM_GROWTH_SLUG]: {
    name: "Growth",
    price: "$599/mo",
    tokens: "5M tokens included",
    refillPrice: "$10 per 1M refill",
  },
  [NUTRITION_PLATFORM_PRO_SLUG]: {
    name: "Pro",
    price: "$2,999/mo",
    tokens: "100M tokens included",
    refillPrice: "$2.50 per 1M refill",
  },
};

const GLOBAL_ROUTE_AVG_DAYS = 30;
const DEFAULT_ROUTE_CALCULATOR_CATEGORIES = [
  "Vision",
  "Text & Audio",
  "AI Search",
  "API Calls",
];
const DEFAULT_ROUTE_CALLS_PER_USER_PER_DAY: Record<string, number> = {
  Vision: 1,
  "Text & Audio": 2,
  "AI Search": 5,
  "API Calls": 20,
};

type RouteAverageRow = {
  category: string;
  totalCalls: number;
  avgBilledTokens: number;
  totalBilledTokens: number;
};

type RawRouteAverageRow = {
  category?: string;
  totalCalls?: number | string;
  avgBilledTokens?: number | string;
  totalBilledTokens?: number | string;
  total_calls?: number | string;
  avg_billed_tokens?: number | string;
  total_billed_tokens?: number | string;
};

const toNumber = (v: unknown): number => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
};

const normalizeRouteCategory = (category: string): string => {
  const normalized = category.trim().toLowerCase();
  if (normalized === "semantic search") {
    return "AI Search";
  }
  return category;
};

const normalizeRouteAverageRows = (raw: unknown): RouteAverageRow[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => row as RawRouteAverageRow)
    .map((row) => ({
      category: normalizeRouteCategory(row.category ?? ""),
      totalCalls: toNumber(row.totalCalls ?? row.total_calls),
      avgBilledTokens: toNumber(row.avgBilledTokens ?? row.avg_billed_tokens),
      totalBilledTokens: toNumber(
        row.totalBilledTokens ?? row.total_billed_tokens
      ),
    }))
    .filter((row) => row.category !== "" && row.totalCalls > 0);
};

const formatTokenCount = (tokens: number): string => {
  if (!Number.isFinite(tokens) || tokens <= 0) return "0";
  if (tokens >= 1000000) {
    return `${(tokens / 1000000).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}M`;
  }
  if (tokens >= 1000) {
    return `${(tokens / 1000).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}K`;
  }
  return Math.round(tokens).toLocaleString();
};

const ManageSubscriptionPlatform = () => {
  const navigate = useNavigate();
  const search = window.location.search;
  const params = new URLSearchParams(search);
  const productQuery = params.get("product") || "";

  const [unauthorized, setUnauthorized] = useState(false);
  const [customerStatus, setCustomerStatus] = useState<CustomerStatusType>({
    hasOrg: false,
    hasPaymentMethod: false,
  });
  const [isSubscribed, setIsSubscribed] = useState<boolean | null>(null);
  const [currentTier, setCurrentTier] = useState<string | null>(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState<number | null>(null);
  const [hasActiveHubSubscription, setHasActiveHubSubscription] =
    useState(false);
  const [hubCurrentPeriodEnd, setHubCurrentPeriodEnd] = useState<number | null>(
    null
  );
  const [hubMigrationMode, setHubMigrationMode] = useState<
    "end_of_cycle" | "immediate"
  >("end_of_cycle");

  const [unsubscribeModalIsOpen, setUnsubscribeModalIsOpen] = useState(false);
  const [upgradeConfirmOpen, setUpgradeConfirmOpen] = useState(false);
  const [pendingTier, setPendingTier] = useState<PlatformTierConfig | null>(
    null
  );

  const [subscriptionModal, setSubscriptionModal] =
    useState<SubscriptionModalType>({
      isOpen: false,
      planSlug: null,
      shouldUpdate: false,
    });

  const [globalRouteAverages, setGlobalRouteAverages] = useState<
    RouteAverageRow[]
  >([]);

  const [couponCode, setCouponCode] = useState("");
  const [couponInfo, setCouponInfo] = useState<CouponInfo | null>(null);
  const [couponValidating, setCouponValidating] = useState(false);
  const [estimatorTier, setEstimatorTier] =
    useState<PlatformTierConfig["tier"]>("growth");
  const [estimatorUsers, setEstimatorUsers] = useState(100);
  const [showCheckoutEstimator, setShowCheckoutEstimator] = useState(false);
  const [estimatorCallsPerUserPerDay, setEstimatorCallsPerUserPerDay] =
    useState<Record<string, number>>({});

  const userContext = useContext(UserContext);

  const { isLoading, isFetching, refetch } = useQuery(
    ["platformManageProducts"],
    () => axios.get("/accounts/products"),
    {
      enabled: !!userContext,
      onError: (err: AxiosError) => {
        error(err?.response?.data?.cause || "Could not fetch products");
        if (err?.response?.status === 403) setUnauthorized(true);
      },
      onSuccess: (res: AxiosResponse) => {
        setCustomerStatus({
          hasOrg: res?.data?.hasOrg,
          hasPaymentMethod: res?.data?.hasPaymentMethod,
        });
        const dynamics = res?.data?.dynamicVolumeSubscriptions || [];
        const hubItem = dynamics.find(
          (item: any) => item?.info?.PackageName === "Nutrition Hub"
        );
        const hubStripeSub = hubItem?.stripeSubscription || null;
        const hubIsSubscribed = Boolean(hubItem?.isSubscribed && hubStripeSub);
        setHasActiveHubSubscription(hubIsSubscribed);
        setHubCurrentPeriodEnd(
          hubIsSubscribed && hubStripeSub?.current_period_end
            ? Number(hubStripeSub.current_period_end)
            : null
        );
        if (!hubIsSubscribed) {
          setHubMigrationMode("end_of_cycle");
        }

        const platformItem = dynamics.find(
          (item: any) => item?.info?.PackageName === "Nutrition Platform"
        );
        setIsSubscribed(platformItem?.isSubscribed ?? false);

        if (platformItem?.isSubscribed && platformItem?.stripeSubscription) {
          const sub = platformItem.stripeSubscription;
          if (sub.metadata?.tier) {
            setCurrentTier(sub.metadata.tier);
          }
          setCancelAtPeriodEnd(sub.cancel_at_period_end ?? false);
          if (sub.current_period_end) {
            setCurrentPeriodEnd(sub.current_period_end);
          }
        }
      },
    }
  );

  const { isLoading: routeAvgLoading, isFetching: routeAvgFetching } = useQuery(
    ["globalRouteAverages", GLOBAL_ROUTE_AVG_DAYS],
    () =>
      axios.get(
        `/accounts/products/nutrition-platform/usage/route-averages/global?days=${GLOBAL_ROUTE_AVG_DAYS}`
      ),
    {
      enabled: true,
      onSuccess: (res: AxiosResponse) =>
        setGlobalRouteAverages(normalizeRouteAverageRows(res?.data)),
    }
  );

  const calculatorCategories = useMemo(
    () =>
      (globalRouteAverages.length > 0
        ? globalRouteAverages.map((row) => row.category)
        : DEFAULT_ROUTE_CALCULATOR_CATEGORIES
      ).filter((value, index, all) => all.indexOf(value) === index),
    [globalRouteAverages]
  );

  useEffect(() => {
    if (
      currentTier === "starter" ||
      currentTier === "growth" ||
      currentTier === "pro"
    ) {
      setEstimatorTier(currentTier);
    }
  }, [currentTier]);

  useEffect(() => {
    setEstimatorCallsPerUserPerDay((prev) => {
      let changed = false;
      const next: Record<string, number> = {};
      calculatorCategories.forEach((category) => {
        if (Object.prototype.hasOwnProperty.call(prev, category)) {
          next[category] = Math.max(0, Math.floor(Number(prev[category]) || 0));
        } else {
          changed = true;
          next[category] = DEFAULT_ROUTE_CALLS_PER_USER_PER_DAY[category] ?? 0;
        }
      });
      if (Object.keys(prev).length !== calculatorCategories.length) {
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [calculatorCategories]);

  const estimatorTierConfig =
    PLATFORM_TIERS.find((tier) => tier.tier === estimatorTier) ||
    PLATFORM_TIERS[1];
  const estimatorUsersSafe = Math.max(
    0,
    Math.floor(Number(estimatorUsers) || 0)
  );
  const routeAvgTokenMap = new Map(
    globalRouteAverages.map((row) => [
      row.category,
      Math.max(0, row.avgBilledTokens),
    ])
  );
  const estimatorRows = calculatorCategories.map((category) => {
    const callsPerUserPerDay = Math.max(
      0,
      Math.floor(Number(estimatorCallsPerUserPerDay[category]) || 0)
    );
    const avgTokensPerCall = routeAvgTokenMap.get(category) || 0;
    const totalCallsPerDay = estimatorUsersSafe * callsPerUserPerDay;
    const tokensPerDay = totalCallsPerDay * avgTokensPerCall;
    return {
      category,
      callsPerUserPerDay,
      avgTokensPerCall,
      totalCallsPerDay,
      tokensPerDay,
    };
  });
  const estimatorTotals = estimatorRows.reduce(
    (acc, row) => ({
      callsPerDay: acc.callsPerDay + row.totalCallsPerDay,
      tokensPerDay: acc.tokensPerDay + row.tokensPerDay,
    }),
    { callsPerDay: 0, tokensPerDay: 0 }
  );
  const estimatorProjectedMonthlyTokens = estimatorTotals.tokensPerDay * 30;
  const estimatorIncludedTokens = estimatorTierConfig.tokensMillions * 1000000;
  const estimatorProjectedOverageTokens = Math.max(
    0,
    estimatorProjectedMonthlyTokens - estimatorIncludedTokens
  );
  const estimatorProjectedOverageCost =
    (estimatorProjectedOverageTokens / 1000000) *
    estimatorTierConfig.refillPricePerM;
  const estimatorReferenceValue =
    (estimatorProjectedMonthlyTokens / 1000000) *
    estimatorTierConfig.refillPricePerM;
  const estimatorIncludedUsagePct =
    estimatorIncludedTokens > 0
      ? Math.min(
          100,
          (estimatorProjectedMonthlyTokens / estimatorIncludedTokens) * 100
        )
      : 0;

  const changeTier = useMutation(
    async (newTier: string) => {
      return await axios.post(
        `/accounts/products/nutrition-platform/subscription/upgrade`,
        { newTier }
      );
    },
    {
      onError: (err: AxiosError) => {
        error(err?.response?.data?.cause || "Something went wrong");
      },
      onSuccess: () => {
        success("Subscription tier changed successfully");
        setUpgradeConfirmOpen(false);
        setPendingTier(null);
        refetch();

        ReactGA.event({
          action: "platform_tier_changed",
          category: `${NUTRITION_PLATFORM_SLUG}_${currentTier}`,
          label: uniqueEventId(),
        });
      },
    }
  );

  const cancelSubscription = useMutation(
    async () =>
      await axios.post(
        `/accounts/products/nutrition-platform/subscription/cancel`
      ),
    {
      onError: (err: AxiosError) => {
        error(err?.response?.data?.cause || "Something went wrong");
      },
      onSuccess: (res: AxiosResponse) => {
        success(
          "Subscription scheduled for cancellation at the end of your billing period"
        );
        setUnsubscribeModalIsOpen(false);
        setCancelAtPeriodEnd(true);
        if (res?.data?.cancelDate) {
          setCurrentPeriodEnd(res.data.cancelDate);
        }
        refetch();

        ReactGA.event({
          action: "platform_subscription_cancelled",
          category: `${NUTRITION_PLATFORM_SLUG}_${currentTier}`,
          label: uniqueEventId(),
        });
      },
    }
  );

  const validateCoupon = useCallback(async () => {
    const trimmed = couponCode.trim();
    if (!trimmed) return;
    setCouponValidating(true);
    try {
      const res = await axios.post(
        "/accounts/products/nutrition-platform/coupon/validate",
        { code: trimmed }
      );
      const data: CouponInfo = res.data;
      setCouponInfo(data);
      if (data.valid) {
        success("Coupon applied successfully");
      } else {
        error(data.message || "Invalid coupon code");
      }
    } catch {
      error("Could not validate coupon code");
      setCouponInfo(null);
    } finally {
      setCouponValidating(false);
    }
  }, [couponCode]);

  const clearCoupon = useCallback(() => {
    setCouponCode("");
    setCouponInfo(null);
  }, []);

  const getDiscountedPrice = (priceInDollars: number): number | null => {
    if (!couponInfo?.valid) return null;
    if (couponInfo.percentOff && couponInfo.percentOff > 0) {
      return (
        Math.round(priceInDollars * (1 - couponInfo.percentOff / 100) * 100) /
        100
      );
    }
    if (couponInfo.amountOff && couponInfo.amountOff > 0) {
      const amountOffDollars = couponInfo.amountOff / 100;
      return Math.max(
        0,
        Math.round((priceInDollars - amountOffDollars) * 100) / 100
      );
    }
    return null;
  };

  const formatPrice = (price: number): string => {
    return price.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: price % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    });
  };

  const durationLabel = (info: CouponInfo): string => {
    switch (info.duration) {
      case "once":
        return "first month only";
      case "repeating":
        return `for ${info.durationInMonths} month${
          (info.durationInMonths ?? 0) > 1 ? "s" : ""
        }`;
      case "forever":
        return "ongoing";
      default:
        return "";
    }
  };

  if (unauthorized) return <Navigate to="/access-restricted" />;

  if (isLoading || isFetching || isSubscribed === null) {
    return <Spinner />;
  }

  // ── Subscribed: show tier management page ──────────────────────────────
  if (isSubscribed) {
    const currentTierIndex = PLATFORM_TIERS.findIndex(
      (t) => t.tier === currentTier
    );

    const isCurrentTier = (idx: number) => idx === currentTierIndex;

    const buttonTextForTier = (idx: number): string => {
      if (idx === currentTierIndex) return "";
      if (idx > currentTierIndex) return "Upgrade";
      return "Downgrade";
    };

    const buttonOnClickForTier = (
      tierConfig: PlatformTierConfig,
      idx: number
    ) => {
      if (idx === currentTierIndex) return () => {};
      return () => {
        setPendingTier(tierConfig);
        setUpgradeConfirmOpen(true);
      };
    };

    const actionWord =
      pendingTier && currentTierIndex >= 0
        ? PLATFORM_TIERS.indexOf(pendingTier) > currentTierIndex
          ? "Upgrade"
          : "Downgrade"
        : "Change";

    return (
      <Fragment>
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-bold inter-bold text-md md:text-lg xl:text-3xl">
              Nutrition AI Platform &mdash; Manage Subscription
            </h2>
          </div>

          {changeTier.isLoading || cancelSubscription.isLoading ? (
            <Spinner />
          ) : (
            <>
              {/* Cancellation banner */}
              {cancelAtPeriodEnd && currentPeriodEnd && (
                <div className="mb-6 p-4 bg-amber-50 border border-amber-300 rounded-lg">
                  <p className="text-amber-800 font-semibold">
                    Subscription cancelling on:{" "}
                    {new Date(currentPeriodEnd * 1000).toLocaleDateString(
                      undefined,
                      {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      }
                    )}
                  </p>
                  <p className="text-amber-700 text-sm mt-1">
                    Your API key and Easy AI access will remain active until
                    this date. After that, your API key will become invalid and
                    no further charges will be made.
                  </p>
                </div>
              )}

              <div className="flex flex-wrap place-content-evenly items-stretch h-full gap-y-4">
                {PLATFORM_TIERS.map((tierConfig, index) => (
                  <SubscriptionCard
                    key={tierConfig.tier}
                    name={tierConfig.name}
                    price={
                      <>
                        <div className="text-gray-900 font-inter text-6xl font-extrabold leading-[60px]">
                          {tierConfig.priceDisplay}
                        </div>
                        <div className="text-gray-500 font-inter text-2xl font-medium leading-8">
                          /mo
                        </div>
                      </>
                    }
                    counterInput={<></>}
                    description={tierConfig.description}
                    descriptionAlt={tierConfig.refillPrice}
                    bulletsHtml={[
                      <span key={`${tierConfig.tier}-included-tokens`}>
                        <span className="font-extrabold">
                          {tierConfig.tokensMillions}M{" "}
                        </span>
                        <span>tokens per month included</span>
                      </span>,
                      ...tierConfig.features.map((feature) => (
                        <span
                          key={`${tierConfig.tier}-${feature.label}`}
                          className="inline-flex items-center"
                        >
                          <span>{feature.label}</span>
                          <FeatureTooltip text={feature.tooltip} />
                        </span>
                      )),
                    ]}
                    bullets={[]}
                    isCurrent={isCurrentTier(index)}
                    buttonText={buttonTextForTier(index)}
                    buttonOnClick={buttonOnClickForTier(tierConfig, index)}
                    isCurrentButtonText="Product Details"
                    isCurrentButtonOnClick={() =>
                      navigate(`/details?product=${NUTRITION_PLATFORM_SLUG}`)
                    }
                    hideButton={cancelAtPeriodEnd && !isCurrentTier(index)}
                    showCancelButton={
                      isCurrentTier(index) && !cancelAtPeriodEnd
                    }
                    cancelButtonOnClick={() => setUnsubscribeModalIsOpen(true)}
                    disabled={
                      cancelAtPeriodEnd ||
                      isLoading ||
                      isFetching ||
                      changeTier.isLoading ||
                      cancelSubscription.isLoading
                    }
                    wide={false}
                    compact
                  />
                ))}
              </div>

              <Card className="sm:p-6 mt-8">
                <p className="font-semibold text-gray-900 mb-1">
                  Monthly Usage Estimator (Calls to Tokens)
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  Enter expected calls per user per day and active users. This
                  estimates monthly token usage and projected refill overage.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1">
                      Estimate Against Tier
                    </label>
                    <select
                      value={estimatorTier}
                      onChange={(e) =>
                        setEstimatorTier(
                          e.target.value as PlatformTierConfig["tier"]
                        )
                      }
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    >
                      {PLATFORM_TIERS.map((tier) => (
                        <option key={tier.tier} value={tier.tier}>
                          {tier.name} ({tier.tokensMillions}M included,{" "}
                          {tier.refillPrice})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1">
                      Active Users
                    </label>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={estimatorUsers}
                      onChange={(e) =>
                        setEstimatorUsers(
                          Math.max(0, Number(e.target.value) || 0)
                        )
                      }
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1">
                      Refill Rate Used
                    </label>
                    <div className="h-[42px] rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 flex items-center">
                      {estimatorTierConfig.refillPrice}
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-100 text-left text-gray-600 text-xs uppercase tracking-wider">
                        <th className="py-2 pr-4">Route Category</th>
                        <th className="py-2 pr-4 text-right">
                          Avg Tokens / Call
                        </th>
                        <th className="py-2 pr-4 text-right">Calls/User/Day</th>
                        <th className="py-2 pr-4 text-right">
                          Total Calls/Day
                        </th>
                        <th className="py-2 text-right">
                          Estimated Tokens/Day
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {estimatorRows.map((row) => (
                        <tr
                          key={row.category}
                          className="border-b border-gray-100 hover:bg-gray-50"
                        >
                          <td className="py-2.5 pr-4 text-gray-900">
                            {row.category}
                          </td>
                          <td className="py-2.5 pr-4 text-right text-gray-700">
                            {Math.round(row.avgTokensPerCall).toLocaleString()}
                          </td>
                          <td className="py-2.5 pr-4 text-right">
                            <input
                              type="number"
                              min={0}
                              step={1}
                              value={row.callsPerUserPerDay}
                              onChange={(e) =>
                                setEstimatorCallsPerUserPerDay((prev) => ({
                                  ...prev,
                                  [row.category]: Math.max(
                                    0,
                                    Math.floor(Number(e.target.value) || 0)
                                  ),
                                }))
                              }
                              className="w-24 rounded-md border border-gray-300 px-2 py-1 text-sm text-right text-gray-900 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                            />
                          </td>
                          <td className="py-2.5 pr-4 text-right text-gray-700">
                            {Math.round(row.totalCallsPerDay).toLocaleString()}
                          </td>
                          <td className="py-2.5 text-right font-medium text-gray-900">
                            {formatTokenCount(row.tokensPerDay)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-gray-300 font-semibold text-gray-900">
                        <td className="py-2.5 pr-4">Total / day</td>
                        <td className="py-2.5 pr-4 text-right">—</td>
                        <td className="py-2.5 pr-4 text-right">—</td>
                        <td className="py-2.5 pr-4 text-right">
                          {Math.round(
                            estimatorTotals.callsPerDay
                          ).toLocaleString()}
                        </td>
                        <td className="py-2.5 text-right">
                          {formatTokenCount(estimatorTotals.tokensPerDay)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                    <p className="text-xs uppercase tracking-wider text-gray-500">
                      Estimated Tokens / 30 Days
                    </p>
                    <p className="mt-1 text-lg font-semibold text-gray-900">
                      {formatTokenCount(estimatorProjectedMonthlyTokens)}
                    </p>
                  </div>
                  <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                    <p className="text-xs uppercase tracking-wider text-gray-500">
                      Included Tokens / 30 Days
                    </p>
                    <p className="mt-1 text-lg font-semibold text-gray-900">
                      {formatTokenCount(estimatorIncludedTokens)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {estimatorIncludedUsagePct.toFixed(1)}% of included used
                    </p>
                  </div>
                  <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                    <p className="text-xs uppercase tracking-wider text-gray-500">
                      Projected Refill Overage / 30 Days
                    </p>
                    <p className="mt-1 text-lg font-semibold text-gray-900">
                      {formatTokenCount(estimatorProjectedOverageTokens)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatPrice(estimatorProjectedOverageCost)} at{" "}
                      {estimatorTierConfig.refillPrice}
                    </p>
                  </div>
                </div>

                <EstimatedMonthlyCost
                  tierName={estimatorTierConfig.name}
                  subscriptionPrice={estimatorTierConfig.price}
                  includedTokensMillions={estimatorTierConfig.tokensMillions}
                  projectedOverageCost={estimatorProjectedOverageCost}
                />

                <p className="mt-3 text-sm text-gray-500">
                  Reference value for all estimated usage (before included
                  tokens): {formatPrice(estimatorReferenceValue)}. Actual refill
                  billing applies only to usage above included monthly tokens.
                </p>
              </Card>

              {/* Estimated cost per 1K calls by route type */}
              <Card className="sm:p-6 mt-6">
                <p className="font-semibold text-gray-900 mb-1">
                  Route Cost Benchmarks
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  {globalRouteAverages.length > 0
                    ? `Shows estimated refill value for 1,000 calls using platform averages from the last ${GLOBAL_ROUTE_AVG_DAYS} days.`
                    : "Loading platform-wide average token usage\u2026"}
                </p>
                <div className="mb-4 rounded-md border border-indigo-100 bg-indigo-50 px-3 py-2 text-sm text-indigo-900">
                  <span className="font-semibold">Reference only:</span> this
                  table is for planning. Refill charges apply only after
                  included monthly tokens are exhausted.
                </div>
                <div className="overflow-visible">
                  <table className="w-full text-sm table-fixed">
                    <thead className="bg-gray-100">
                      <tr className="border-b border-gray-200 text-left text-gray-600 text-xs">
                        <th className="py-2 pr-3 w-[18%]">Route Type</th>
                        <th className="py-2 pr-3 text-right w-[24%]">
                          <span className="inline-flex items-center justify-end">
                            Avg Tokens / Call (30d)
                            <FeatureTooltip text="Average billed tokens for one request in this route category, based on platform-wide usage from the last 30 days." />
                          </span>
                        </th>
                        {PLATFORM_TIERS.map((t) => (
                          <th
                            key={t.tier}
                            className="py-2 pl-2 text-right w-[19.3%]"
                          >
                            <span className="inline-flex items-center justify-end gap-0.5">
                              {t.name}
                              <FeatureTooltip
                                text={`${t.name} plan (${t.priceDisplay}/mo) includes ${t.tokensMillions}M tokens. Extra tokens cost ${t.refillPrice} per 1M refill. This column shows what 1,000 API calls would cost at the ${t.name} refill rate if those tokens were billed as overage.`}
                              />
                            </span>
                            <br />
                            <span className="text-[11px] font-normal text-gray-500">
                              per 1k calls ({t.refillPrice}/1M)
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {globalRouteAverages
                        .map((r) => ({
                          route: r.category,
                          avgTokens: Math.round(r.avgBilledTokens),
                        }))
                        .map((row) => (
                          <tr
                            key={row.route}
                            className="border-b border-gray-100 hover:bg-gray-50"
                          >
                            <td className="py-2.5 pr-3 text-gray-900">
                              {row.route}
                            </td>
                            <td className="py-2.5 pr-3 text-right font-medium text-indigo-600">
                              {routeAvgLoading || routeAvgFetching
                                ? "..."
                                : `~${row.avgTokens.toLocaleString()}`}
                            </td>
                            {PLATFORM_TIERS.map((t) => (
                              <td
                                key={t.tier}
                                className="py-2.5 pl-2 text-right text-gray-700"
                              >
                                {(
                                  (row.avgTokens * t.refillPricePerM * 1000) /
                                  1000000
                                ).toLocaleString("en-US", {
                                  style: "currency",
                                  currency: "USD",
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </td>
                            ))}
                          </tr>
                        ))}
                      {!routeAvgLoading &&
                        !routeAvgFetching &&
                        globalRouteAverages.length === 0 && (
                          <tr>
                            <td
                              colSpan={2 + PLATFORM_TIERS.length}
                              className="py-4 text-center text-sm text-gray-500"
                            >
                              No route-average data available yet.
                            </td>
                          </tr>
                        )}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-gray-300">
                        <td
                          colSpan={2 + PLATFORM_TIERS.length}
                          className="pt-3 text-sm text-gray-400"
                        >
                          Calls with billed tokens &gt;= 2,500,000 are excluded
                          as outliers.
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </Card>
            </>
          )}
        </div>

        {/* Tier change confirmation modal */}
        <Modal
          isOpen={upgradeConfirmOpen}
          setOpen={setUpgradeConfirmOpen}
          title={`${actionWord} to ${pendingTier?.name}?`}
          disabled={changeTier.isLoading}
          isLoading={changeTier.isLoading}
          description={`You will be ${actionWord.toLowerCase()}d to the ${
            pendingTier?.name
          } plan at ${pendingTier?.priceDisplay}/month with ${
            pendingTier?.tokensMillions
          }M tokens included. Your billing will be prorated.`}
          submitButtonName={`Confirm ${actionWord}`}
          buttonType="primary"
          onSubmitted={() => {
            if (pendingTier) {
              changeTier.mutate(pendingTier.tier);
            }
          }}
        />

        {/* Cancel subscription modal */}
        <Modal
          isOpen={unsubscribeModalIsOpen}
          setOpen={setUnsubscribeModalIsOpen}
          title="Cancel Subscription?"
          disabled={cancelSubscription.isLoading}
          isLoading={cancelSubscription.isLoading}
          description={
            "Are you sure you want to cancel your Nutrition AI Platform subscription? " +
            "Your API key will become invalid and your Easy AI access will end as soon as " +
            "your current billing cycle ends. You will not be charged again, but you can " +
            "continue using the service until then."
          }
          submitButtonName="Cancel Subscription"
          buttonType="warning"
          type="error"
          onSubmitted={() => cancelSubscription.mutate()}
        />
      </Fragment>
    );
  }

  // ── Not subscribed: checkout flow ──────────────────────────────────────

  const tierSlug = productQuery;
  const tierInfo = TIER_CHECKOUT_INFO[tierSlug];
  const tierName = tierSlug.replace("nutrition-platform-", "");

  // Base slug with no tier → redirect to detail page
  if (!tierInfo) {
    return <Navigate to={`/details?product=${NUTRITION_PLATFORM_SLUG}`} />;
  }

  function closeSubscriptionModal() {
    refetch();
    setSubscriptionModal({
      isOpen: false,
      planSlug: null,
      shouldUpdate: false,
    });
  }

  const tierConfig = PLATFORM_TIERS.find((t) => t.tier === tierName);
  const rawPrice = tierConfig?.price ?? 0;
  const discountedPrice = getDiscountedPrice(rawPrice);
  const hasDiscount = discountedPrice !== null && discountedPrice !== rawPrice;
  const effectiveDiscountedPrice = discountedPrice ?? rawPrice;
  const hubCycleEndLabel =
    hubCurrentPeriodEnd && Number.isFinite(hubCurrentPeriodEnd)
      ? new Date(hubCurrentPeriodEnd * 1000).toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : null;

  return (
    <Fragment>
      <div className="mt-4">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Subscribe to Nutrition AI Platform &mdash;{" "}
          <span className="text-indigo-600">{tierInfo.name}</span>
        </h2>
        <p className="text-gray-500 mb-6 text-sm">
          {hasDiscount ? (
            <>
              <span className="line-through">{tierInfo.price}</span>{" "}
              <span className="text-green-600 font-semibold">
                {formatPrice(effectiveDiscountedPrice)}/mo
              </span>
            </>
          ) : (
            tierInfo.price
          )}{" "}
          &middot; {tierInfo.tokens} &middot; {tierInfo.refillPrice}
        </p>

        {hasActiveHubSubscription && (
          <Card className="sm:p-5 mb-6 border border-indigo-200 bg-indigo-50/60">
            <p className="text-base font-semibold text-indigo-900">
              Migrate from Nutrition AI Hub
            </p>
            <p className="mt-1 text-sm text-indigo-800">
              Your API key stays the same. Choose whether Hub ends now or at
              cycle end.
            </p>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="rounded-md border border-indigo-300 bg-white px-3 py-2 cursor-pointer">
                <div className="flex items-start gap-2">
                  <input
                    type="radio"
                    name="hubMigrationMode"
                    value="end_of_cycle"
                    checked={hubMigrationMode === "end_of_cycle"}
                    onChange={() => setHubMigrationMode("end_of_cycle")}
                    className="mt-1"
                  />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      End at cycle end (recommended)
                    </p>
                    <p className="text-sm text-gray-600">
                      Hub stays active until{" "}
                      {hubCycleEndLabel || "the current cycle end"}; Platform
                      starts at that boundary.
                    </p>
                  </div>
                </div>
              </label>
              <label className="rounded-md border border-indigo-300 bg-white px-3 py-2 cursor-pointer">
                <div className="flex items-start gap-2">
                  <input
                    type="radio"
                    name="hubMigrationMode"
                    value="immediate"
                    checked={hubMigrationMode === "immediate"}
                    onChange={() => setHubMigrationMode("immediate")}
                    className="mt-1"
                  />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      Migrate immediately
                    </p>
                    <p className="text-sm text-gray-600">
                      Hub is cancelled now and Platform starts now.
                    </p>
                  </div>
                </div>
              </label>
            </div>
          </Card>
        )}

        <div className="grid lg:grid-cols-5 gap-6">
          <Card className="lg:col-span-2 sm:p-6 h-fit">
            <h3 className="font-semibold text-gray-900 mb-4">Plan Summary</h3>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Plan</dt>
                <dd className="font-medium text-gray-900">{tierInfo.name}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Monthly Price</dt>
                <dd className="font-medium text-gray-900">
                  {hasDiscount ? (
                    <>
                      <span className="line-through text-gray-400 mr-2">
                        {tierInfo.price}
                      </span>
                      <span className="text-green-600">
                        {formatPrice(effectiveDiscountedPrice)}/mo
                      </span>
                    </>
                  ) : (
                    tierInfo.price
                  )}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Included Tokens</dt>
                <dd className="font-medium text-gray-900">{tierInfo.tokens}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Refill Rate</dt>
                <dd className="font-medium text-gray-900">
                  {tierInfo.refillPrice}
                </dd>
              </div>
            </dl>

            {/* Coupon Code Section */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Coupon Code
              </label>
              {couponInfo?.valid ? (
                <div className="bg-green-50 border border-green-200 rounded-md p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-800">
                        {couponInfo.name || couponCode.trim()}
                      </p>
                      <p className="text-xs text-green-600 mt-0.5">
                        {couponInfo.percentOff
                          ? `${couponInfo.percentOff}% off`
                          : couponInfo.amountOff
                          ? `${formatPrice(couponInfo.amountOff / 100)} off`
                          : "Discount applied"}
                        {" \u2014 "}
                        {durationLabel(couponInfo)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={clearCoupon}
                      className="text-green-600 hover:text-green-800 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => {
                      setCouponCode(e.target.value);
                      if (couponInfo) setCouponInfo(null);
                    }}
                    placeholder="Enter coupon code"
                    className="flex-1 h-[36px] rounded-md border-0 py-1 px-3 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-500"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        validateCoupon();
                      }
                    }}
                  />
                  <Button
                    type="button"
                    onClick={validateCoupon}
                    disabled={!couponCode.trim() || couponValidating}
                    isLoading={couponValidating}
                    outlined
                    className="!text-sm !px-3 !py-1"
                  >
                    Verify
                  </Button>
                </div>
              )}
              {couponInfo && !couponInfo.valid && (
                <p className="text-xs text-red-500 mt-1">
                  {couponInfo.message || "Invalid coupon code"}
                </p>
              )}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-400">
                Includes Easy AI, SDK access, localization, and the full REST
                API. Auto-refill enabled by default.
              </p>
            </div>
          </Card>

          <CheckoutForm
            productPlanSlug={tierSlug}
            shouldCreateOrg={!customerStatus.hasOrg}
            shouldCapturePayment={!customerStatus.hasPaymentMethod}
            shouldUpdateSubscription={false}
            subscriptionData={{
              tier: tierName,
              refillBehaviour: "auto",
              refillBatchQuantity: 1,
              ...(hasActiveHubSubscription
                ? { hubMigrationMode: hubMigrationMode }
                : {}),
              ...(couponInfo?.valid && couponInfo.promotionCodeId
                ? { promotionCodeId: couponInfo.promotionCodeId }
                : {}),
              ...(couponInfo?.valid &&
              !couponInfo.promotionCodeId &&
              couponInfo.couponId
                ? { couponCode: couponInfo.couponId }
                : {}),
            }}
          />
        </div>

        <Card className="sm:p-0 mb-6 mt-6 border border-indigo-200 shadow-sm overflow-hidden">
          <button
            type="button"
            className="w-full px-4 py-3 flex items-center justify-between bg-indigo-50 hover:bg-indigo-100 transition-colors"
            onClick={() => setShowCheckoutEstimator((prev) => !prev)}
            aria-expanded={showCheckoutEstimator}
          >
            <div className="text-left">
              <p className="font-semibold text-indigo-900">
                Monthly Usage Estimator (Calls to Tokens)
              </p>
              <p className="text-xs text-indigo-700">
                Optional planning tool for token usage and overage.
              </p>
            </div>
            <span className="text-sm font-semibold text-indigo-700">
              {showCheckoutEstimator ? "Hide" : "Show"}
            </span>
          </button>

          {showCheckoutEstimator && (
            <div className="p-6">
              <p className="text-sm text-gray-500 mb-4">
                Use this before checkout to estimate monthly token usage and
                projected refill overage for your expected user traffic.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1">
                    Estimate Against Tier
                  </label>
                  <select
                    value={estimatorTier}
                    onChange={(e) =>
                      setEstimatorTier(
                        e.target.value as PlatformTierConfig["tier"]
                      )
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  >
                    {PLATFORM_TIERS.map((tier) => (
                      <option key={tier.tier} value={tier.tier}>
                        {tier.name} ({tier.tokensMillions}M included,{" "}
                        {tier.refillPrice})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1">
                    Active Users
                  </label>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={estimatorUsers}
                    onChange={(e) =>
                      setEstimatorUsers(
                        Math.max(0, Number(e.target.value) || 0)
                      )
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1">
                    Refill Rate Used
                  </label>
                  <div className="h-[42px] rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 flex items-center">
                    {estimatorTierConfig.refillPrice}
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-100 text-left text-gray-600 text-xs uppercase tracking-wider">
                      <th className="py-2 pr-4">Route Category</th>
                      <th className="py-2 pr-4 text-right">
                        Avg Tokens / Call
                      </th>
                      <th className="py-2 pr-4 text-right">Calls/User/Day</th>
                      <th className="py-2 pr-4 text-right">Total Calls/Day</th>
                      <th className="py-2 text-right">Estimated Tokens/Day</th>
                    </tr>
                  </thead>
                  <tbody>
                    {estimatorRows.map((row) => (
                      <tr
                        key={row.category}
                        className="border-b border-gray-100 hover:bg-gray-50"
                      >
                        <td className="py-2.5 pr-4 text-gray-900">
                          {row.category}
                        </td>
                        <td className="py-2.5 pr-4 text-right text-gray-700">
                          {Math.round(row.avgTokensPerCall).toLocaleString()}
                        </td>
                        <td className="py-2.5 pr-4 text-right">
                          <input
                            type="number"
                            min={0}
                            step={1}
                            value={row.callsPerUserPerDay}
                            onChange={(e) =>
                              setEstimatorCallsPerUserPerDay((prev) => ({
                                ...prev,
                                [row.category]: Math.max(
                                  0,
                                  Math.floor(Number(e.target.value) || 0)
                                ),
                              }))
                            }
                            className="w-24 rounded-md border border-gray-300 px-2 py-1 text-sm text-right text-gray-900 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                          />
                        </td>
                        <td className="py-2.5 pr-4 text-right text-gray-700">
                          {Math.round(row.totalCallsPerDay).toLocaleString()}
                        </td>
                        <td className="py-2.5 text-right font-medium text-gray-900">
                          {formatTokenCount(row.tokensPerDay)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-300 font-semibold text-gray-900">
                      <td className="py-2.5 pr-4">Total / day</td>
                      <td className="py-2.5 pr-4 text-right">—</td>
                      <td className="py-2.5 pr-4 text-right">—</td>
                      <td className="py-2.5 pr-4 text-right">
                        {Math.round(
                          estimatorTotals.callsPerDay
                        ).toLocaleString()}
                      </td>
                      <td className="py-2.5 text-right">
                        {formatTokenCount(estimatorTotals.tokensPerDay)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs uppercase tracking-wider text-gray-500">
                    Estimated Tokens / 30 Days
                  </p>
                  <p className="mt-1 text-lg font-semibold text-gray-900">
                    {formatTokenCount(estimatorProjectedMonthlyTokens)}
                  </p>
                </div>
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs uppercase tracking-wider text-gray-500">
                    Included Tokens / 30 Days
                  </p>
                  <p className="mt-1 text-lg font-semibold text-gray-900">
                    {formatTokenCount(estimatorIncludedTokens)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {estimatorIncludedUsagePct.toFixed(1)}% of included used
                  </p>
                </div>
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs uppercase tracking-wider text-gray-500">
                    Projected Refill Overage / 30 Days
                  </p>
                  <p className="mt-1 text-lg font-semibold text-gray-900">
                    {formatTokenCount(estimatorProjectedOverageTokens)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatPrice(estimatorProjectedOverageCost)} at{" "}
                    {estimatorTierConfig.refillPrice}
                  </p>
                </div>
              </div>
              <EstimatedMonthlyCost
                tierName={estimatorTierConfig.name}
                subscriptionPrice={estimatorTierConfig.price}
                includedTokensMillions={estimatorTierConfig.tokensMillions}
                projectedOverageCost={estimatorProjectedOverageCost}
              />
              <p className="mt-3 text-xs text-gray-500">
                Reference value for all estimated usage (before included
                tokens): {formatPrice(estimatorReferenceValue)}. Actual refill
                billing applies only to usage above included monthly tokens.
              </p>
            </div>
          )}
        </Card>
      </div>

      <SubscriptionModal
        isOpen={subscriptionModal.isOpen}
        closeModal={closeSubscriptionModal}
        closeBtn={true}
        shouldCreateOrg={!customerStatus.hasOrg}
        shouldCapturePayment={!customerStatus.hasPaymentMethod}
        shouldUpdate={subscriptionModal.shouldUpdate}
        planSlug={subscriptionModal.planSlug || ""}
        subscriptionData={{}}
      />
    </Fragment>
  );
};

export default ManageSubscriptionPlatform;
